import React, { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import { Document, Page } from "react-pdf/dist/umd/entry.webpack";
import { Button, Divider, Grid, Message } from "semantic-ui-react";
import AnnotTypeMenu from "./annots/AnnotTypeMenu";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { addAnnotToCanvas } from "./annots/annotUtils";
import DocPrepareActionButtons from "./DocPrepareActionButtons";
import DocPrepareSignerList from "./signerList/DocPrepareSignerList";
import AnnotEditButtons from "./annots/AnnotEditButtons";
import AnnotDraggableField from "./annots/AnnotDraggableField";

import {
  createSigningParties,
  createSignerListFromAnnots,
} from "./annots/annotUtils";
import { roleIsOtherAgentsClients } from "./sendForSigning/sendForSigningUtils";
import {
  updateDocInDb,
} from "../../../../app/firestore/firestoreService";
import { notifySignerToSign } from "./signerList/signatureNotificationUtils";
import FormFieldSelector from "./formFields/FormFieldSelector";
import LoadingComponent from "../../../../app/layout/LoadingComponent";
import {
  addAnnot,
  changeSelectedSigner,
  selectDocAnnot,
  setSignerListDisplay,
  setSignerListPossible,
  fetchAnnots,
  changeActiveAnnotType,
  clearPendingSignaturePlacement,
} from "../../../../app/annots/annotSlice";
import {
  createAllSuggestedAnnots,
  // createSuggestedAnnots,
  getAndSavePdfDimensions,
  getFormFieldValues,
  partyIsBuyer,
  partyIsSeller,
} from "../../../../app/common/util/util";
import DocViewAgentAnnotField from "../docView/DocViewAgentAnnotField";
import DocViewSignedAnnotField from "../docView/DocViewSignedAnnotField";
import { getCurrentUserIpAddress } from "../../../profiles/profileSlice";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { Rnd } from "react-rnd";

const options = {
  cMapUrl: "cmaps/",
  cMapPacked: true,
  standardFontDataUrl: "standard_fonts/",
};

const SIGNATURE_DATE_GAP = 8;
const SIGNATURE_DATE_WIDTH = 50;

// Places the date box immediately to the right of the signature box, aligned to the same baseline.
function getDateLocationForSignature({ left, top, width, height, fontSize }) {
  return {
    dateLeft: left + width + SIGNATURE_DATE_GAP,
    dateTop: top,
    dateWidth: SIGNATURE_DATE_WIDTH,
    dateHeight: height,
    dateFontSize: fontSize,
  };
}

export function DocPrepareDashboard() {
  const isMobile = useMediaQuery({ query: "(max-width:1056px)" });
  const dispatch = useDispatch();
  const [numPages, setNumPages] = useState(null);
  const {
    annots,
    pageScalePrepare,
    activeAnnotType,
    selectedSigner,
    editMode,
    signerListDisplay,
    pendingSignaturePlacement,
  } = useSelector((state) => state.annot);
  const { doc, docUrl } = useSelector((state) => state.doc);
  const { currentUserProfile, currentUserIpAddress } = useSelector(
    (state) => state.profile
  );
  const { transClients, transaction, parties } = useSelector(
    (state) => state.transaction
  );
  const [formFieldValues, setFormFieldValues] = useState();
  const [updatedSuggestedAnnots, setUpdatedSuggestedAnnots] = useState(false);
  const [signaturePlacementDrafts, setSignaturePlacementDrafts] = useState({});
  const signaturePlacementDraftsRef = useRef({});
  const [pageDimensions, setPageDimensions] = useState(
    doc.dimensions || {
      width: 612,
      height: 792,
    }
  );
  const pageScale = pageScalePrepare;
  const processedPartiesRef = useRef(new Set());
  const agentSignatureLocations = doc?.agentSignatureLocations;
  const docFormFieldValues = doc?.formFieldValues;

  // Forms designated "One Side Only" are meant to show just the Buyer's OR
  // just the Seller's signature/date/checkbox annots, never both, so admins
  // authoring the form can still see everything they've placed, but regular
  // (non-admin) users preparing an actual document only ever see the side
  // relevant to who the agent represents -- avoiding confusing overlapping
  // annots for the excluded side.
  const isOneSideOnlyForm = doc?.annotsInProgressSuggestedMode === "oneSideOnly";
  const visibleAnnots =
    isOneSideOnlyForm && !currentUserProfile?.isAdmin
      ? annots?.filter((annot) => {
          if (transaction?.agentRepresents === "Buyer") {
            return !partyIsSeller(annot.signerRole);
          }
          if (transaction?.agentRepresents === "Seller") {
            return !partyIsBuyer(annot.signerRole);
          }
          return true;
        })
      : annots;

  useEffect(() => {
    signaturePlacementDraftsRef.current = signaturePlacementDrafts;
  }, [signaturePlacementDrafts]);

  useEffect(() => {
    const savedLocations = agentSignatureLocations || {};
    const savedDrafts = Object.entries(savedLocations).reduce(
      (drafts, [roleKey, location]) => {
        if (!location || docFormFieldValues?.[location.signatureFieldName]) {
          return drafts;
        }

        drafts[roleKey] = {
          ...location,
          roleKey,
          label: roleKey === "coagent" ? "Co-Agent" : "Agent",
          recipientId:
            roleKey === "coagent" ? transaction?.coAgentId : transaction?.userId,
          notifyOnSave: false,
          ...(location.dateLeft == null ? getDateLocationForSignature(location) : {}),
        };
        return drafts;
      },
      {}
    );

    setSignaturePlacementDrafts((currentDrafts) => {
      const unsignedCurrentDrafts = Object.entries(currentDrafts).reduce(
        (drafts, [roleKey, draft]) => {
          if (!docFormFieldValues?.[draft.signatureFieldName]) {
            drafts[roleKey] = draft;
          }
          return drafts;
        },
        {}
      );
      const nextDrafts = {
        ...savedDrafts,
        ...unsignedCurrentDrafts,
      };
      signaturePlacementDraftsRef.current = nextDrafts;
      return nextDrafts;
    });
  }, [agentSignatureLocations, docFormFieldValues, transaction]);

  useEffect(() => {
    if (currentUserProfile?.hasSigningVerification && !currentUserIpAddress)
      axios.get("https://api.ipify.org?format=json").then((res) => {
        dispatch(getCurrentUserIpAddress(res?.data?.ip));
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let partiesCopy = JSON.parse(JSON.stringify(parties));
    let transactionCopy = JSON.parse(JSON.stringify(transaction));
    const signingParties = createSigningParties(transactionCopy, partiesCopy);
    dispatch(
      setSignerListPossible(_.sortBy(signingParties.possibleSigners, "index"))
    );

    if (doc.signerListInProgress?.length > 0) {
      // Filter saved signer list to only include parties that still exist and have names
      const filteredSignerList = doc.signerListInProgress.filter((signer) => {
        // For agent's own clients, check if they have names
        const isAgentClient = transClients.some(
          (client) => client.role === signer.role
        );
        if (isAgentClient) {
          const client = transClients.find(
            (client) => client.role === signer.role
          );
          return client && client.firstName && client.lastName;
        }

        // For other parties, check if they exist in parties array and have names
        const party = parties.find((p) => p.role === signer.role);
        return party && party.firstName && party.lastName;
      });

      dispatch(setSignerListDisplay(filteredSignerList));
    } else {
      // Start with the default display signers (agent's own clients) but filter out those without names
      let displaySigners = [];
      if (signingParties && signingParties.displaySigners?.length > 0) {
         displaySigners = signingParties.displaySigners.filter((signer) => {
          // For agent's own clients, only include if they have names
          const isAgentClient = transClients.some(
            (client) => client.role === signer.role
          );
          if (isAgentClient) {
            const client = transClients.find(
              (client) => client.role === signer.role
            );
            return client && client.firstName && client.lastName;
          }
          return true; // Keep non-client signers for now
        });
      }

      // If document has annotations, add parties that have annotations to the display list
      if (doc.annotsInProgress && doc.annotsInProgress.length > 0) {
        // const signerRolesWithAnnots = createSignerListFromAnnots(
        //   doc.annotsInProgress
        // );
        // For shared documents, only consider unsigned annotations for the agentRepresents side clients
        const annotsToConsider = doc.shared
          ? doc.annotsInProgress.filter((annot) => !annot.signed)
          : doc.annotsInProgress;
        const signerRolesWithAnnots = createSignerListFromAnnots(
          annotsToConsider
        );

        // Add parties with annotations to display list if they're not already there
        signerRolesWithAnnots.forEach((role) => {
          if (!displaySigners.find((signer) => signer.role === role)) {
            const partyWithAnnots = signingParties.possibleSigners.find(
              (signer) => signer.role === role
            );
            if (partyWithAnnots) {
              // Only add if the party actually exists and has names
              const isAgentClient = transClients.some(
                (client) => client.role === role
              );
              if (isAgentClient) {
                const client = transClients.find(
                  (client) => client.role === role
                );
                if (client && client.firstName && client.lastName) {
                  displaySigners.push(partyWithAnnots);
                }
              } else {
                const party = parties.find((p) => p.role === role);
                if (party && party.firstName && party.lastName) {
                  displaySigners.push(partyWithAnnots);
                }
              }
            }
          }
        });
      }

      dispatch(setSignerListDisplay(_.sortBy(displaySigners, "index")));
    }

    if (
      doc.selectedSignerInProgress &&
      !_.isEmpty(doc.selectedSignerInProgress)
    ) {
      dispatch(changeSelectedSigner(doc.selectedSignerInProgress));
    } else {
      dispatch(changeSelectedSigner(transClients[0]));
    }
  }, [doc, dispatch, parties, transClients, transaction]);

  useEffect(() => {}, [pageScalePrepare, annots]);

  useEffect(() => {
    getFormFieldValues(doc, transaction, setFormFieldValues);
  }, [doc, transaction]);

  useEffect(() => {
    if (
      doc &&
      doc.annotsInProgressSuggestedAdded === false &&
      updatedSuggestedAnnots === false &&
      !doc.shared
    ) {
      let suggestedAnnots = [];
      if (doc?.annotsInProgressSuggested) {
        suggestedAnnots = createAllSuggestedAnnots(transaction, doc, parties);
        setUpdatedSuggestedAnnots(true);
        updateDocInDb(
          doc.id,
          {
            annotsInProgress: suggestedAnnots,
            annotsInProgressSuggestedAdded: true,
          },
          false
        );
      }
    }
  }, [doc, updatedSuggestedAnnots, transaction, parties]);

  // For shared documents, filter annotsInProgress to only include annotations
  // for roles that actually exist in the receiver's transaction parties
  // AND have not already signed (check signingDetails)
  const [filteredSharedAnnots, setFilteredSharedAnnots] = useState(false);
  useEffect(() => {
    if (
      doc &&
      doc.shared &&
      doc.annotsInProgress?.length > 0 &&
      !filteredSharedAnnots &&
      (transClients.length > 0 || parties.length > 0)
    ) {
      // Get all valid roles from the receiver's transaction
      const validRoles = [
        ...transClients.map((c) => c.role),
        ...parties.map((p) => p.role),
      ];

      // Get roles that have already signed from signingDetails
      const signedRoles = [];
      if (doc.signingDetails && typeof doc.signingDetails === "object") {
        Object.values(doc.signingDetails).forEach((detail) => {
          if (detail.signed === true && detail.role) {
            signedRoles.push(detail.role);
          }
        });
      }

      // Filter annotations to only include those for valid roles that haven't signed
      const filteredAnnots = doc.annotsInProgress.filter((annot) => {
        // Always keep agent's own fields
        if (annot.agentsField) return true;
        // Exclude annotations for roles that have already signed
        if (signedRoles.includes(annot.signerRole)) return false;
        // Only keep annotations for roles that exist in this transaction
        return validRoles.includes(annot.signerRole);
      });

      // Only update if we actually filtered something out
      if (filteredAnnots.length !== doc.annotsInProgress.length) {
        console.log(
          "Filtering shared doc annotations from",
          doc.annotsInProgress.length,
          "to",
          filteredAnnots.length,
          "- signed roles:",
          signedRoles
        );
        updateDocInDb(
          doc.id,
          {
            annotsInProgress: filteredAnnots,
          },
          false
        );
      }
      setFilteredSharedAnnots(true);
    }
  }, [doc, transClients, parties, filteredSharedAnnots]);

  // Handle adding annotations for newly added parties and removing annotations for deleted parties
  useEffect(() => {
    if (
      doc &&
      doc.annotsInProgressSuggestedAdded === true &&
      doc?.annotsInProgressSuggested &&
      !doc.shared &&
      parties.length > 0
    ) {
      // Get all current party roles including agent's own clients and other parties
      const currentPartyRoles = [
        ...parties.map((p) => p.role), // Other parties (Seller, Title Company, etc.)
        ...transClients.map((c) => c.role), // Agent's own clients (Buyer, Buyer 2, etc.)
      ];
      const currentAnnots = doc.annotsInProgress || [];

      // Initialize processedPartiesRef on first run with parties that already have annotations
      if (processedPartiesRef.current.size === 0) {
        const partiesWithAnnots = new Set();
        currentAnnots.forEach((annot) => {
          if (annot.signerRole && !annot.agentsField) {
            partiesWithAnnots.add(annot.signerRole);
          }
        });
        processedPartiesRef.current = partiesWithAnnots;
      }

      // Check for deleted parties (parties that were processed but no longer exist)
      const deletedPartyRoles = Array.from(processedPartiesRef.current).filter(
        (role) => !currentPartyRoles.includes(role)
      );

      // Check for new parties that haven't been processed
      const newPartyRoles = currentPartyRoles.filter(
        (role) => !processedPartiesRef.current.has(role)
      );

      let needsUpdate = false;
      let updatedAnnots = [...currentAnnots];

      // Remove annotations for deleted parties
      if (deletedPartyRoles.length > 0) {
        updatedAnnots = updatedAnnots.filter(
          (annot) => !deletedPartyRoles.includes(annot.signerRole)
        );
        // Remove deleted parties from processed set
        deletedPartyRoles.forEach((role) =>
          processedPartiesRef.current.delete(role)
        );
        needsUpdate = true;
      }

      // Add annotations for new parties
      if (newPartyRoles.length > 0) {
        // Get suggested annotations ONLY for the newly added parties
        let newPartyAnnots =
          doc.annotsInProgressSuggested?.filter((annot) =>
            newPartyRoles.includes(annot.signerRole)
          ) || [];

        // "One Side Only" forms should never pick up the non-represented
        // side's annots, even if that party gets added to the transaction
        // later -- otherwise Buyer's and Seller's signature lines end up
        // overlapping on the same document.
        if (doc.annotsInProgressSuggestedMode === "oneSideOnly") {
          if (transaction.agentRepresents === "Buyer") {
            newPartyAnnots = newPartyAnnots.filter(
              (annot) => !partyIsSeller(annot.signerRole)
            );
          } else if (transaction.agentRepresents === "Seller") {
            newPartyAnnots = newPartyAnnots.filter(
              (annot) => !partyIsBuyer(annot.signerRole)
            );
          }
        }

        if (newPartyAnnots.length > 0) {
          updatedAnnots = [...updatedAnnots, ...newPartyAnnots];
          needsUpdate = true;
        }

        // Mark these parties as processed
        newPartyRoles.forEach((role) => processedPartiesRef.current.add(role));
      }

      // Update document if changes were made
      if (needsUpdate) {
        // Update Redux state with new annotations
        dispatch(fetchAnnots(updatedAnnots));

        // Update document in database
        updateDocInDb(
          doc.id,
          {
            annotsInProgress: updatedAnnots,
          },
          false
        );
      }
    }
  }, [doc, transaction, parties, transClients, dispatch]);

  async function onDocumentLoadSuccess(pdfObject) {
    getAndSavePdfDimensions(
      doc,
      pdfObject,
      setNumPages,
      setPageDimensions,
      "agent"
    );
  }

  function getSignatureLocationForDb(draft) {
    const { roleKey, ...signatureLocation } = draft;
    delete signatureLocation.label;
    delete signatureLocation.recipientId;
    delete signatureLocation.notifyOnSave;
    return signatureLocation;
  }

  async function createSignaturePlacementDraft(canvas, event, pageIndex) {
    if (!pendingSignaturePlacement) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = (event.clientX - rect.left) / pageScale;
    const clickY = (event.clientY - rect.top) / pageScale;
    const fontSize = 11;
    const height = 18;
    const signatureLocation = {
      left: clickX,
      top: clickY - (height - fontSize),
      width: 180,
      height,
      fontSize,
    };
    const draft = {
      ...pendingSignaturePlacement,
      notifyOnSave: false,
      page: pageIndex,
      ...signatureLocation,
      signatureFieldName: pendingSignaturePlacement.signatureFieldName,
      dateFieldName: pendingSignaturePlacement.dateFieldName,
      ...getDateLocationForSignature(signatureLocation),
    };

    setSignaturePlacementDrafts((currentDrafts) => {
      const nextDrafts = {
        ...currentDrafts,
        [draft.roleKey]: draft,
      };
      signaturePlacementDraftsRef.current = nextDrafts;
      return nextDrafts;
    });

    try {
      await updateDocInDb(doc.id, {
        [`agentSignatureLocations.${draft.roleKey}`]: getSignatureLocationForDb(
          draft
        ),
      });
      await notifySignerToSign({
        transaction,
        doc,
        currentUserProfile,
        roleKey: draft.roleKey,
        recipientProfile: draft.recipientProfile,
      });
      toast.success(`${draft.label} notified to sign.`);
      dispatch(clearPendingSignaturePlacement());
    } catch (error) {
      toast.error(error.message || "Failed to notify signer.");
    }
  }

  const clearSignaturePlacement = useCallback(() => {
    signaturePlacementDraftsRef.current = {};
    setSignaturePlacementDrafts({});
    dispatch(clearPendingSignaturePlacement());
  }, [dispatch]);

  function updateSignaturePlacementDraftPosition(roleKey, data) {
    setSignaturePlacementDrafts((currentDrafts) => {
      const currentDraft = currentDrafts[roleKey];
      if (!currentDraft) return currentDrafts;

      const updatedLocation = {
        left: data.x / pageScale,
        top: data.y / pageScale - (currentDraft.height - currentDraft.fontSize),
        width: currentDraft.width,
        height: currentDraft.height,
        fontSize: currentDraft.fontSize,
      };
      const nextDrafts = {
        ...currentDrafts,
        [roleKey]: {
          ...currentDraft,
          ...updatedLocation,
          ...getDateLocationForSignature(updatedLocation),
        },
      };
      signaturePlacementDraftsRef.current = nextDrafts;
      return nextDrafts;
    });
  }

  function getSignaturePlacementDraftsFromDom() {
    return Object.entries(signaturePlacementDraftsRef.current).reduce(
      (drafts, [roleKey, draft]) => {
        const boxElement = document.getElementById(
          `signature-placement-box-${roleKey}`
        );
        const pageContainer = document.getElementById(
          `signature-page-container-${draft.page}`
        );

        if (!boxElement || !pageContainer) {
          drafts[roleKey] = draft;
          return drafts;
        }

        const boxRect = boxElement.getBoundingClientRect();
        const pageRect = pageContainer.getBoundingClientRect();
        const renderedLeft = boxRect.left - pageRect.left;
        const renderedTop = boxRect.top - pageRect.top;

        const updatedLocation = {
          left: renderedLeft / pageScale,
          top: renderedTop / pageScale - (draft.height - draft.fontSize),
          width: draft.width,
          height: draft.height,
          fontSize: draft.fontSize,
        };
        drafts[roleKey] = {
          ...draft,
          ...updatedLocation,
          ...getDateLocationForSignature(updatedLocation),
        };
        return drafts;
      },
      {}
    );
  }

  function getSignatureLocationUpdates() {
    const latestDraftsByRole = getSignaturePlacementDraftsFromDom();
    signaturePlacementDraftsRef.current = latestDraftsByRole;
    const drafts = Object.values(latestDraftsByRole);
    if (drafts.length === 0) return {};

    const signatureLocationUpdates = drafts.reduce((updates, draft) => {
      updates[`agentSignatureLocations.${draft.roleKey}`] = getSignatureLocationForDb(
        draft
      );
      return updates;
    }, {});

    setSignaturePlacementDrafts(() => {
      const nextDrafts = Object.entries(latestDraftsByRole).reduce(
        (drafts, [roleKey, draft]) => {
          drafts[roleKey] = {
            ...draft,
            notifyOnSave: false,
          };
          return drafts;
        },
        {}
      );
      signaturePlacementDraftsRef.current = nextDrafts;
      return nextDrafts;
    });
    dispatch(clearPendingSignaturePlacement());
    return signatureLocationUpdates;
  }

  function Canvas({ pageIndex, ...props }) {
    const canvasRef = useRef(null);
    useEffect(() => {
      const canvas = canvasRef.current;
      if (canvas) canvas.getContext("2d");
    }, []);

    function handleCanvasDrop(e) {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;

      const annot = addAnnotToCanvas(
        canvas,
        e,
        activeAnnotType,
        doc,
        selectedSigner,
        currentUserProfile,
        currentUserIpAddress,
        transaction,
        pageScale,
        editMode
      );
      dispatch(addAnnot(annot));
      dispatch(selectDocAnnot(annot));
      dispatch(changeActiveAnnotType(null));
    }

    function handleCanvasDragOver(e) {
      e.stopPropagation();
      e.preventDefault();
    }

    function handleCanvasClick(e) {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;

      if (pendingSignaturePlacement) {
        createSignaturePlacementDraft(canvas, e, pageIndex);
        return;
      }

      if (!activeAnnotType) return;

      const annot = addAnnotToCanvas(
        canvas,
        e,
        activeAnnotType,
        doc,
        selectedSigner,
        currentUserProfile,
        currentUserIpAddress,
        transaction,
        pageScale,
        editMode
      );
      if (!annot) return;

      dispatch(addAnnot(annot));
      dispatch(selectDocAnnot(annot));
      dispatch(changeActiveAnnotType(null));
    }

    return (
      <canvas
        ref={canvasRef}
        onClick={handleCanvasClick}
        onDrop={handleCanvasDrop}
        onDragOver={handleCanvasDragOver}
        {...props}
      />
    );
  }

  return (
    <div>
      <div
        // className="pdf-left-panel-wrapper large padding"
        className={
          isMobile
            ? "pdf-left-panel-wrapper-mobile small padding"
            : "pdf-left-panel-wrapper large padding"
        }
      >
        <Grid>
          <Grid.Column width={16}>
            <div className="large vertical margin">
              <p
                style={{
                  color: "#9a9a9a",
                  marginBottom: "3px",
                  marginTop: "30px",
                }}
              >
                Signer
              </p>
              <Divider className="zero top margin" />
              <DocPrepareSignerList />
            </div>
            <AnnotEditButtons />
          </Grid.Column>
        </Grid>
      </div>
      <div
        className={
          isMobile ? "pdf-annot-menu-wrapper-mobile" : "pdf-annot-menu-wrapper"
        }
        id="annot-menu"
      >
        <AnnotTypeMenu />
      </div>
      <div
        className={
          isMobile ? "pdf-prepare-wrapper-mobile" : "pdf-prepare-wrapper"
        }
        style={{
          backgroundColor: "#f3f6f8",
        }}
      >
        {(pendingSignaturePlacement || Object.keys(signaturePlacementDrafts).length > 0) && (
          <Message info style={{ margin: "10px 20px" }}>
            {Object.keys(signaturePlacementDrafts).length > 0
              ? pendingSignaturePlacement
                ? `Click where the ${pendingSignaturePlacement.label} should sign. Existing signature boxes can still be dragged.`
                : "Drag the signature boxes into position, then click Save button at the bottom."
              : `Click where the ${pendingSignaturePlacement.label} should sign.`}
            {pendingSignaturePlacement && (
              <Button
              size="mini"
              floated="right"
              onClick={clearSignaturePlacement}
            >
              Cancel
            </Button>
            )}
          </Message>
        )}
        <div className="pdf-document-wrapper">
          <div
            style={{
              position: "relative",
            }}
          >
            <Document
              file={docUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <div style={{ height: window.innerHeight }}>
                  <LoadingComponent />
                </div>
              }
              options={options}
            >
              {Array.from(new Array(numPages), (_, index) => (
                <div
                  className="pdf-prepare-page-wrapper large vertical padding"
                  key={`page-${index}`}
                  id={`page-${index}`}
                >
                  <div className="pdf-page-number">
                    Page {index + 1}/{numPages}
                  </div>
                  <div
                    className="pdf-page-container"
                        id={`signature-page-container-${index}`}
                    style={{
                      height: `${pageDimensions.height * pageScale}px`,
                      width: `${pageDimensions.width * pageScale}px`,
                    }}
                  >
                    <Page
                      scale={pageScale}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                    />
                    <Canvas
                      className="pdf-canvas"
                      id={`canvas-${index}`}
                      pageIndex={index}
                      style={{
                        width: `${pageDimensions.width * pageScale}px`,
                        height: `${pageDimensions.height * pageScale}px`,
                      }}
                    />
                    {Object.values(signaturePlacementDrafts)
                      .filter((draft) => draft.page === index)
                      .map((draft) => (
                        <Rnd
                          key={`${draft.roleKey}-${draft.page}-${pageScale}`}
                          id={`signature-placement-box-${draft.roleKey}`}
                          bounds={`#canvas-${index}`}
                          default={{
                            x: draft.left * pageScale,
                            y:
                              (draft.top + draft.height - draft.fontSize) *
                              pageScale,
                            width: draft.width * pageScale,
                            height: draft.height * pageScale,
                          }}
                          onDragStop={(_event, data) => {
                            updateSignaturePlacementDraftPosition(
                              draft.roleKey,
                              data
                            );
                          }}
                          enableResizing={false}
                        >
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              backgroundColor: "rgba(255, 220, 0, 0.35)",
                              border: "2px solid rgba(220, 160, 0, 0.8)",
                              borderRadius: "3px",
                              cursor: "move",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#8a6500",
                              fontSize: `${draft.fontSize * pageScale}px`,
                              fontFamily: "Brush Script MT, cursive",
                              zIndex: 20,
                            }}
                          >
                            {draft.roleKey === "coagent"
                              ? "Co-Agent Signature"
                              : "Agent Signature"}
                          </div>
                        </Rnd>
                      ))}
                    {Object.values(signaturePlacementDrafts)
                      .filter((draft) => draft.page === index && draft.dateLeft != null)
                      .map((draft) => (
                        <div
                          key={`${draft.roleKey}-date-${draft.page}-${pageScale}`}
                          title="Date auto-added after signature"
                          style={{
                            position: "absolute",
                            top: `${
                              (draft.dateTop + draft.dateHeight - draft.dateFontSize) *
                              pageScale
                            }px`,
                            left: `${draft.dateLeft * pageScale}px`,
                            width: `${draft.dateWidth * pageScale}px`,
                            height: `${draft.dateHeight * pageScale}px`,
                            backgroundColor: "rgba(33, 133, 208, 0.15)",
                            border: "1px dashed rgba(33, 133, 208, 0.8)",
                            borderRadius: "3px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#2185d0",
                            fontSize: `${draft.dateFontSize * pageScale}px`,
                            zIndex: 19,
                            pointerEvents: "none",
                          }}
                        >
                          Date
                        </div>
                      ))}
                    {numPages &&
                      formFieldValues?.map((formField) => (
                        <React.Fragment key={formField.name}>
                          {formField.page === index && (
                            <FormFieldSelector
                              formField={formField}
                              pageScale={pageScale}
                            />
                          )}
                        </React.Fragment>
                      ))}
                    {numPages &&
                      visibleAnnots?.map((annot) => (
                        <React.Fragment key={annot.uniqueId}>
                          {annot.page === index &&
                            (!roleIsOtherAgentsClients(
                              annot.signerRole,
                              transaction
                            ) ||
                              annot.agentsField ||
                              signerListDisplay.find(
                                (signer) => signer.role === annot.signerRole
                              )) && (
                              <AnnotDraggableField
                                annot={annot}
                                pageScale={pageScale}
                                transaction={transaction}
                              />
                            )}
                        </React.Fragment>
                      ))}
                    {numPages &&
                      visibleAnnots?.map((annot) => (
                        <React.Fragment key={`other-${annot.uniqueId}`}>
                          {annot.page === index &&
                            roleIsOtherAgentsClients(
                              annot.signerRole,
                              transaction
                            ) &&
                            !annot.agentsField &&
                            !signerListDisplay.find(
                              (signer) => signer.role === annot.signerRole
                            ) && (
                              <AnnotDraggableField
                                annot={annot}
                                pageScale={pageScale}
                                transaction={transaction}
                              />
                            )}
                        </React.Fragment>
                      ))}
                    {numPages &&
                      doc.pdfBurnVersion &&
                      doc.annotsByAgent &&
                      Object.entries(doc.annotsByAgent).map(([key, value]) => (
                        <React.Fragment key={key}>
                          {value.page === index && (
                            <DocViewAgentAnnotField
                              annot={value}
                              pageScale={pageScale}
                            />
                          )}
                        </React.Fragment>
                      ))}
                    {numPages &&
                      doc.pdfBurnVersion &&
                      doc.annotsToSign &&
                      Object.entries(doc.annotsToSign).map(([key, value]) => (
                        <React.Fragment key={key}>
                          {value.page === index && (
                            <>
                              {value.signed === true && (
                                <DocViewSignedAnnotField
                                  annot={value}
                                  pageScale={pageScale}
                                />
                              )}
                            </>
                          )}
                        </React.Fragment>
                      ))}
                  </div>
                </div>
              ))}
            </Document>
          </div>
        </div>
      </div>
      <Grid className="zero horizontal margin zero horizontal padding">
        <Grid.Column width={16} className="zero horizontal padding">
          <DocPrepareActionButtons
            getSignatureLocationUpdates={getSignatureLocationUpdates}
          />
        </Grid.Column>
      </Grid>
    </div>
  );
}
